import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from './config';
import { pin } from './workingSet';
import TagPicker from './TagPicker';
import { Explain } from './Explain';

// The hand capture modal — R-A, one step (records/READER_WALK_PREP.md, driven
// 2026-08-07: "R-A one-step popover", "a tag step, anchor, and author included
// in the minting modal").
//
// ONE component for every surface, by owner ruling on open question #2. The
// Reader and ObjectDetail both render this, and it posts to the same door;
// ObjectDetail's old hand-rolled gesture (mint + derived_from + navigate away,
// with no tag, no anchor and no vote) is deleted rather than left beside it.
// Two gestures that mint the same thing differently is the failure the ruling
// names.
//
// What the ruling ALSO settled, and is easy to lose: the mint carries the
// author's +1 (on `derived_from`, and on any tag they picked), which is
// deliberately unlike an AI suggestion. The user is asserting that this claim
// is in that text. The copy says so rather than leaving it to be inferred from
// a vote count changing.
//
// RELATING IS ABSENT ON PURPOSE (2026-08-04 ruling, restated 2026-08-06):
// drawing does not belong in a reading surface. The minted claim pins to the
// working set and /triage is where relations get drawn.

// Where the modal goes for a given selection. Shared by both callers, because
// the same three lines diverging on two surfaces is how R-A quietly becomes
// R-C on one of them — which is exactly what happened before this was
// extracted: ObjectDetail rendered a bottom sheet on desktop, and R-C ("bottom
// sheet on desktop too") is the option the owner did NOT pick.
//
// Phones get the sheet because there is no margin to float into; that is the
// 2026-07-25 mobile pass's rule and it applies to every surface.
export function placeFromSelection(sel) {
  if (window.matchMedia('(max-width: 640px)').matches) return { sheet: true };
  const rect = sel.getRangeAt(0).getBoundingClientRect();
  // 400 of headroom: quote + textarea + tag field + actions + the visibility
  // note. At 320 the note clipped behind a scrollbar (measured 2026-08-07).
  const y = Math.min(rect.bottom + 8, window.innerHeight - 400);
  return {
    position: {
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 432)),
      top: y,
      maxHeight: window.innerHeight - y - 16,
    },
  };
}

export default function CaptureClaim({ sourceId, quote, onDone, onClose, sheet = false, position, modelAccess }) {
  const [text, setText] = useState(quote);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // Assisted capture (2026-08-11): which model DRAFTED the textarea's current
  // wording, or null for hand wording. Cleared when the user wipes the box —
  // fresh typing after a wipe is theirs, not the model's; ordinary edits to a
  // proposal keep the label (editing a draft is still writing WITH it).
  const [proposedModel, setProposedModel] = useState(null);
  const [proposing, setProposing] = useState(false);
  const [proposeNote, setProposeNote] = useState(null);

  // The affordance is ABSENT on sealed sources, not disabled — sealed is
  // refusal, and the hand path is the only path there (the Reader's own copy
  // says so). `modelAccess` comes from the surface that already has it.
  const canPropose = modelAccess && modelAccess !== 'sealed';

  const propose = async () => {
    setProposing(true);
    setProposeNote(null);
    try {
      const res = await apiFetch(`/api/read/${sourceId}/claims/propose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quote }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(
          res.status === 503
            ? body.error || 'The model lane for this document is unreachable right now.'
            : body.error || 'Could not propose a claim from this selection'
        );
      }
      if (!body.text) {
        setProposeNote('The model found nothing checkable in this span — write the claim by hand.');
        return;
      }
      setText(body.text);
      setProposedModel(body.model || null);
    } catch (err) {
      setProposeNote(err.message);
    } finally {
      setProposing(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/read/${sourceId}/claims`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The QUOTE goes over the wire, never offsets — the server re-locates
        // it. A client offset can already be stale, and a wrong one does not
        // fail loudly, it underlines the wrong sentence.
        // written_with_model: self-declared model provenance for a proposed
        // wording — the human's authored_by and +1 are untouched by it.
        body: JSON.stringify({
          quote,
          text,
          tags,
          ...(proposedModel ? { written_with_model: proposedModel } : {}),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to capture that claim');
      pin({ id: body.id, content: body.content, object_type: 'claim' });
      setResult(body);
      if (onDone) onDone(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const body = result ? (
    <>
      <span className="rd-pop-state s-quiet">minted · no judgments yet</span>
      <p className="rd-pop-claim">{result.content}</p>
      <p className="cap-note">
        {result.anchored ? (
          <>✓ anchored in this text</>
        ) : (
          // Honest, and specific about the consequence. The claim is real; only
          // the underline is missing, and the reader should not have to guess
          // which of the two failed.
          <>
            ✓ minted, but this span could not be located in the text, so it will
            appear in the “not locatable” list instead of underlined here
          </>
        )}
        {result.tags.length > 0 && <> · tagged {result.tags.map((t) => t.content).join(', ')}</>}
      </p>
      <p className="cap-done">
        ✓ pinned to your working set — <Link to="/triage">relate it on /triage →</Link>
      </p>
    </>
  ) : (
    <>
      <span className="rd-pop-state s-quiet">mint a claim</span>
      <blockquote className="cap-quote">“{quote}”</blockquote>
      <label className="cap-label" htmlFor="cap-text">
        {proposedModel
          ? 'AI-proposed — edit before minting; the quote above stays verbatim as the anchor'
          : 'claim text — the quote above stays verbatim as the anchor'}
      </label>
      {/* NO autoFocus (2026-08-11): focus stealing was half the copy conflict —
          with the textarea focused, Ctrl+C targeted its (empty) caret instead
          of the document selection the reader had just made. Focus arrives on
          first interaction instead. */}
      <textarea
        id="cap-text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) setProposedModel(null);
        }}
        rows={3}
      />
      {canPropose && (
        <div className="cap-propose-row">
          <button className="cap-propose" onClick={propose} disabled={proposing || saving}>
            {proposing ? 'proposing…' : '✦ propose a claim from this selection'}
          </button>
          {proposeNote && <span className="cap-propose-note">{proposeNote}</span>}
        </div>
      )}
      <div className="cap-label">tags (optional)</div>
      <TagPicker tags={tags} onChange={setTags} />
      {error && <p className="cap-error">{error}</p>}
      <div className="cap-actions">
        <button className="cap-mint" onClick={submit} disabled={saving || !text.trim()}>
          {saving ? 'Minting…' : 'Mint claim'}
        </button>
        <button className="cap-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
      <p className="cap-note">
        <Explain concept="model-visibility">
          Visibility is inherited from this document — a claim taken from a sealed document is
          sealed.
        </Explain>{' '}
        · derived from this document, carrying your +1
      </p>
    </>
  );

  return (
    <>
      {sheet && <div className="rd-pop-backdrop" onClick={onClose} />}
      <div
        className={`rd-pop cap-pop${sheet ? ' rd-pop--sheet' : ''}`}
        style={sheet ? undefined : position}
        onClick={(e) => e.stopPropagation()}
      >
        {sheet && (
          <button className="rd-pop-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
        {body}
      </div>
    </>
  );
}
